/**
 * Local persistence and URL bootstrap for Amplitude Experiment **feature-flag overrides**
 * (staff-only forced variants). Not generic Amplitude analytics.
 * Uses the `amplitudeFlags` query param and Amplitude storage keys.
 */

import { getDebugFromCookie } from '@/lib/debug/debug-state';

export const AMPLITUDE_FLAGS_URL_PARAM = 'amplitudeFlags';

const STORAGE_KEYS = {
  FEATURE_FLAG_OVERRIDES_ENABLED:
    'amplitude-experiment-feature-flag-overrides-enabled',
  FEATURE_FLAG_OVERRIDES_MAP: 'amplitude-experiment-feature-flag-overrides-map',
} as const;

export type AmplitudeLocalFlagValues = Record<string, boolean>;

/** True when Surface UI debug is on and staff chose to apply stored override entries. */
export const shouldApplyAmplitudeFeatureFlagOverrides = (): boolean =>
  getDebugFromCookie() && isAmplitudeFeatureFlagOverridesEnabled();

export const isAmplitudeFeatureFlagOverridesEnabled = (): boolean => {
  try {
    return (
      localStorage.getItem(STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_ENABLED) ===
      'true'
    );
  } catch {
    return false;
  }
};

export const setAmplitudeFeatureFlagOverridesEnabled = (
  enabled: boolean
): void => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_ENABLED,
      enabled.toString()
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to set Amplitude feature-flag overrides enabled state:',
      error
    );
  }
};

/**
 * Reads `?amplitudeFlags=...` once: enables override application, merges flags into localStorage, strips param.
 * - `amplitudeFlags=false` — only disables the overrides-enabled flag (does not clear the stored map).
 * - `amplitudeFlags=true` — enables with no parsed flag entries.
 * - `amplitudeFlags=key,key2:false` — enables; omitted value defaults to true.
 */
export const bootstrapAmplitudeFeatureFlagOverridesFromUrl = (): void => {
  const params = new URL(window.location.href).searchParams;
  const raw = params.get(AMPLITUDE_FLAGS_URL_PARAM);
  if (raw === null) return;

  const removeURLParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(AMPLITUDE_FLAGS_URL_PARAM);
    window.history.replaceState({}, document.title, url.toString());
  };

  if (raw === 'false') {
    setAmplitudeFeatureFlagOverridesEnabled(false);
    removeURLParam();
    return;
  }

  if (!isAmplitudeFeatureFlagOverridesEnabled()) {
    setAmplitudeFeatureFlagOverridesEnabled(true);
  }

  const flags: AmplitudeLocalFlagValues = {};
  if (raw !== 'true' && raw.length > 0) {
    raw.split(',').forEach((entry) => {
      const [key, value] = entry.split(':');
      if (key?.trim()) {
        const k = key.trim();
        flags[k] =
          value === undefined ? true : value.trim().toLowerCase() === 'true';
      }
    });
  }

  if (Object.keys(flags).length > 0) {
    const current = getAmplitudeLocalFlagValues();
    setAmplitudeLocalFlagValues({ ...current, ...flags });
  }

  removeURLParam();
};

export const getAmplitudeLocalFlagValues = (): AmplitudeLocalFlagValues => {
  try {
    const stored = localStorage.getItem(
      STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_MAP
    );
    return stored ? (JSON.parse(stored) as AmplitudeLocalFlagValues) : {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read Amplitude feature-flag overrides map:', error);
    return {};
  }
};

export const setAmplitudeLocalFlagValues = (
  flags: AmplitudeLocalFlagValues
): void => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_MAP,
      JSON.stringify(flags)
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to set Amplitude feature-flag overrides map:', error);
  }
};

export const hasAmplitudeLocalFlag = (flagKey: string): boolean => {
  return Object.prototype.hasOwnProperty.call(
    getAmplitudeLocalFlagValues(),
    flagKey
  );
};

export const removeAmplitudeLocalFlag = (flagKey: string): void => {
  const current = getAmplitudeLocalFlagValues();
  delete current[flagKey];
  setAmplitudeLocalFlagValues(current);
};

/** Removes overrides-enabled flag and stored map (e.g. when Surface UI debug is turned off). */
export const clearAmplitudeFeatureFlagOverridesStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_ENABLED);
    localStorage.removeItem(STORAGE_KEYS.FEATURE_FLAG_OVERRIDES_MAP);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to clear Amplitude feature-flag overrides storage:',
      error
    );
  }
};

/**
 * Builds the `amplitudeFlags` query value for sharing when overrides are applying and non-empty.
 */
export const serializeAmplitudeFeatureFlagOverridesForShare = ():
  string | null => {
  if (!shouldApplyAmplitudeFeatureFlagOverrides()) return null;
  const local = getAmplitudeLocalFlagValues();
  const entries = Object.entries(local);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k}:${v}`).join(',');
};
